import React from 'react'
import { Card, Button, Typography, Space, Row, Col } from 'antd'
import {
  FileTextOutlined,
  UserOutlined,
  TeamOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  BarChartOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder } from '@fortawesome/free-solid-svg-icons'
import { useDispatch, useSelector } from 'react-redux'
import coreReducer from '../../../../core/utils/reducer'
import CoreEnums from '../../../../core/utils/enums'
import PatientFileViewer from './PatientFileViewer'

const { Title, Text, Paragraph } = Typography

const PatientFileLanding = () => {
  const dispatch = useDispatch()
  const authState = useSelector((state) => state.auth)
  const currentPatient = authState.agiliteUser

  const handleViewFile = (targetTab = null) => {
    // Open the patient file viewer in a new tab
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.MY_PATIENT_FILE}_viewer`,
        closable: true,
        label: 'My Patient File',
        children: <PatientFileViewer initialTab={targetTab} />
      })
    )
  }

  const handleFeatureClick = (tabKey) => {
    // Open the patient file viewer and navigate to specific tab
    handleViewFile(tabKey)
  }

  const fileFeatures = [
    {
      icon: <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
      title: 'Personal Information',
      description: 'View and manage your personal details, contact information, and medical aid details',
      tabKey: 'patientProfile'
    },
    {
      icon: <TeamOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
      title: 'Family Members',
      description: 'Access dependant profiles and manage family medical information',
      tabKey: 'dependants'
    },
    {
      icon: <MedicineBoxOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
      title: 'Prescriptions',
      description: 'View your current and past prescriptions, dosages, and medication history',
      tabKey: 'scripts'
    },
    {
      icon: <FileTextOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />,
      title: 'Medical Documents',
      description: 'Access lab results, medical certificates, and other important documents',
      tabKey: 'documents'
    },
    {
      icon: <CalendarOutlined style={{ fontSize: '24px', color: '#eb2f96' }} />,
      title: 'Appointment History',
      description: 'View past and upcoming appointments with your healthcare providers',
      tabKey: 'bookings'
    },
    {
      icon: <BarChartOutlined style={{ fontSize: '24px', color: '#13c2c2' }} />,
      title: 'Health Analytics',
      description: 'Track your health trends and view personalized health insights',
      tabKey: 'analytics'
    }
  ]

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header Section */}
      <Card
        style={{
          marginBottom: '24px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(99, 102, 241, 0.8) 100%)',
          border: 'none',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            pointerEvents: 'none'
          }}
        />
        <Row align='middle' justify='space-between' gutter={[16, 8]}>
          <Col flex='auto'>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                position: 'relative',
                zIndex: 1
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  color: 'white',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  flexShrink: 0,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              >
                <FontAwesomeIcon icon={faFolder} />
              </div>
              <div style={{ flex: 1 }}>
                <Title
                  level={2}
                  style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    margin: '0 0 8px 0',
                    lineHeight: '1.2',
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  My Patient File
                </Title>
                <Text
                  style={{
                    fontSize: '16px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    margin: '0',
                    lineHeight: '1.5'
                  }}
                >
                  Welcome, {currentPatient?.firstName} {currentPatient?.lastName}. Access your complete medical
                  information, appointment history, prescriptions, and more in one secure location.
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Quick Action Button */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <Button
          type='primary'
          size='large'
          onClick={() => handleViewFile('patientProfile')}
          style={{
            height: '48px',
            padding: '0 32px',
            borderRadius: '24px',
            fontWeight: 'bold',
            fontSize: '16px',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(99, 102, 241, 0.9) 100%)',
            border: 'none',
            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
            color: 'white',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)'
            e.target.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.4)'
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0px)'
            e.target.style.boxShadow = '0 4px 16px rgba(99, 102, 241, 0.3)'
          }}
        >
          <FileTextOutlined style={{ marginRight: '8px' }} />
          View My File
        </Button>
      </div>

      {/* Features Overview */}
      <Card
        title="What's in Your Patient File"
        style={{ borderRadius: '12px' }}
        headStyle={{
          backgroundColor: '#bae7ff',
          fontWeight: 'bold',
          borderRadius: '12px 12px 0 0',
          borderBottom: '1px solid #91d5ff'
        }}
        bodyStyle={{ backgroundColor: '#e6f7ff', padding: '24px', borderRadius: '0 0 12px 12px' }}
      >
        <Row gutter={[24, 24]}>
          {fileFeatures.map((feature, index) => (
            <Col xs={24} md={12} lg={8} key={index}>
              <Card
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                bodyStyle={{ padding: '20px' }}
                hoverable
                onClick={() => handleFeatureClick(feature.tabKey)}
              >
                <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                  <div style={{ textAlign: 'center' }}>{feature.icon}</div>
                  <Title level={5} style={{ textAlign: 'center', margin: 0 }}>
                    {feature.title}
                  </Title>
                  <Text type='secondary' style={{ textAlign: 'center', display: 'block' }}>
                    {feature.description}
                  </Text>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  )
}

export default PatientFileLanding
